/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useApolloClient } from '@apollo/client';
import { Backdrop, CircularProgress } from '@mui/material';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  AdminMfaStatusDocument,
  CheckAuthStateDocument,
  CheckAuthStateMutation,
  CheckAuthStateMutationVariables,
  CurrentUserAdminPermissionsDocument,
  CurrentUserFragment,
  LoginDocument,
  LoginMutation,
  LoginMutationVariables,
  LogoutDocument,
  LogoutMutation,
  LogoutMutationVariables,
} from '../generated/graphql';
import useSilentMutation from '../graphql/hooks/useSilentMutation';
import { LocaleEnumFe } from '../types';
import { useManagedPolling } from '../ui/hooks/useManagedPolling';
import { isAdminMfaRequiredError, mapBeLocaleToFeLocale } from '../utils';

export interface AdminPermissions {
  canCreateNewVenues?: boolean;
  canMoveVenueToPaywall?: boolean;
  canModifyInvoiceLimit?: boolean;
  canModifyCostRadarTrial?: boolean;
  canConnectStoryousIntegration?: boolean;
  canManageVenues?: boolean;
  canCreateCustomCheckout?: boolean;
  canManageInvoiceBilling?: boolean;
  canManageSupplierIntegrations?: boolean;
  canModifyPaymentFailureGracePeriod?: boolean;
  canMigrateAutoCategorization?: boolean;
}

export interface CurrentUser
  extends Omit<CurrentUserFragment, 'cultureCode' | 'languageCode'> {
  cultureCode: LocaleEnumFe;
  languageCode: LocaleEnumFe;

  adminPermissions: AdminPermissions;
}

export interface AdminMfaGate {
  enrolled: boolean;
  isServiceAccount: boolean;
}

interface AuthContextInterface {
  user: CurrentUser | null;
  mfaGate: AdminMfaGate | null;
  backupCodes: string[] | null;
  login: (loginArgs: LoginMutationVariables) => Promise<void>;
  completeMfa: () => Promise<void>;
  setBackupCodes: (codes: string[] | null) => void;
  isLoginLoading?: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextInterface>({
  user: null,
} as AuthContextInterface);

const LOCAL_STORAGE_USER_KEY = 'user';
const PERMISSIONS_POLL_INTERVAL = 10 * 1000;

const setCurrentUserToLS = (currentUser: CurrentUser) =>
  localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(currentUser));
const unsetCurrentUserFromLS = () =>
  localStorage.removeItem(LOCAL_STORAGE_USER_KEY);

export const getUserFromLS = () => {
  const userFromLs = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
  if (userFromLs) {
    try {
      const parsedUser = JSON.parse(userFromLs) as CurrentUser;
      return parsedUser;
    } catch {
      return null;
    }
  }

  return null;
};

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const apolloClient = useApolloClient();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [mfaGate, setMfaGate] = useState<AdminMfaGate | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const pendingUserRef = useRef<CurrentUserFragment | null>(null);

  const [checkAuthMutation] = useSilentMutation<
    CheckAuthStateMutation,
    CheckAuthStateMutationVariables
  >(CheckAuthStateDocument, { fetchPolicy: 'no-cache' });

  const updateUserInfo = useCallback(
    (
      userPayload: CurrentUserFragment & { adminPermissions: AdminPermissions },
    ) => {
      const { cultureCode, languageCode, ...other } = userPayload;

      const fetchedUser = {
        ...other,
        cultureCode: mapBeLocaleToFeLocale(cultureCode),
        languageCode: mapBeLocaleToFeLocale(languageCode),
      };
      setCurrentUserToLS(fetchedUser);
      setUser(fetchedUser);

      if (
        user &&
        JSON.stringify(userPayload.adminPermissions) !==
          JSON.stringify(user.adminPermissions)
      ) {
        window.location.reload();
      }
    },
    [user],
  );

  const processFetchedUser = useCallback(
    async (
      userPayload: CurrentUserFragment & { adminPermissions: AdminPermissions },
    ) => {
      updateUserInfo(userPayload);
    },
    [updateUserInfo],
  );

  const getUserAdminPermissions = async () =>
    new Promise<AdminPermissions>((resolve, reject) => {
      apolloClient
        .query({
          query: CurrentUserAdminPermissionsDocument,
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => {
          if (data?.adminPermissions) {
            resolve(data.adminPermissions);
          } else {
            reject(new Error('Admin permissions missing'));
          }
        })
        .catch(reject);
    });

  const enterAuthenticatedSession = useCallback(
    async (userPayload: CurrentUserFragment) => {
      const adminPermissions = await getUserAdminPermissions();
      setMfaGate(null);
      setBackupCodes(null);
      pendingUserRef.current = null;
      await processFetchedUser({ ...userPayload, adminPermissions });
    },
    [processFetchedUser],
  );

  const resolveMfaGate = useCallback(
    async (userPayload: CurrentUserFragment) => {
      const { data } = await apolloClient.query({
        query: AdminMfaStatusDocument,
        fetchPolicy: 'no-cache',
      });
      const status = data?.adminMfaStatus;
      if (!status?.isAdmin) {
        setUser(null);
        setMfaGate(null);
        setBackupCodes(null);
        pendingUserRef.current = null;
        unsetCurrentUserFromLS();
        await apolloClient.mutate({ mutation: LogoutDocument });
        throw new Error('Not an admin');
      }

      pendingUserRef.current = userPayload;

      if (!status.enrolled) {
        setUser(null);
        unsetCurrentUserFromLS();
        setMfaGate({
          enrolled: false,
          isServiceAccount: status.isServiceAccount,
        });
        return;
      }

      if (!status.verified) {
        setUser(null);
        unsetCurrentUserFromLS();
        setMfaGate({
          enrolled: true,
          isServiceAccount: status.isServiceAccount,
        });
        return;
      }

      await enterAuthenticatedSession(userPayload);
    },
    [apolloClient, enterAuthenticatedSession],
  );

  const completeMfa = useCallback(async () => {
    const pendingUser = pendingUserRef.current;
    if (!pendingUser) {
      const { data } = await checkAuthMutation();
      if (data?.checkAuthState) {
        await enterAuthenticatedSession(data.checkAuthState);
      }
      return;
    }

    await enterAuthenticatedSession(pendingUser);
  }, [checkAuthMutation, enterAuthenticatedSession]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await checkAuthMutation();
      if (data?.checkAuthState) {
        await resolveMfaGate(data.checkAuthState);
      }
    } catch (err) {
      await logout();
      if (user) {
        toast('You are unauthorized and logged out.', { type: 'error' });
      }
    }
  }, [processFetchedUser, resolveMfaGate]);

  const checkUserPermissions = useCallback(() => {
    if (!user) {
      return;
    }

    getUserAdminPermissions()
      .then((newPermissions) => {
        if (
          JSON.stringify(newPermissions) !==
          JSON.stringify(user?.adminPermissions)
        ) {
          checkAuth();
        }
      })
      .catch(async (err) => {
        if (err.message === 'Failed to fetch') return;
        if (isAdminMfaRequiredError(err)) {
          setUser(null);
          unsetCurrentUserFromLS();
          setMfaGate(
            (current) => current ?? { enrolled: true, isServiceAccount: false },
          );
          return;
        }

        await logout();
        if (user) {
          toast('You are unauthorized and logged out.', { type: 'error' });
        }
      });
  }, [checkAuth, user]);

  const [loginMutation, { loading: isLoginLoading }] = useSilentMutation<
    LoginMutation,
    LoginMutationVariables
  >(LoginDocument);

  const login = async (variables: LoginMutationVariables) => {
    const { data } = await loginMutation({
      variables,
    });
    if (data) {
      await resolveMfaGate(data.login);
    }
  };

  useEffect(() => {
    checkAuth()
      .catch((err) => {
        unsetCurrentUserFromLS();
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const [logoutMutation] = useSilentMutation<
    LogoutMutation,
    LogoutMutationVariables
  >(LogoutDocument);

  const logout = async () => {
    try {
      await logoutMutation();
      setUser(null);
      setMfaGate(null);
      setBackupCodes(null);
      pendingUserRef.current = null;
      unsetCurrentUserFromLS();
    } catch (err) {
      console.error(err);
      setUser(null);
      setMfaGate(null);
      setBackupCodes(null);
      pendingUserRef.current = null;
      unsetCurrentUserFromLS();
    }
  };

  const localStorageListener = (event: StorageEvent) => {
    if (event.key === LOCAL_STORAGE_USER_KEY) {
      const { oldValue, newValue } = event;

      if (!oldValue && newValue) {
        checkAuth().catch((err) => {
          console.error(err);
        });
      }

      if (oldValue && !newValue) {
        setUser(null);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('storage', localStorageListener);
    return () => window.removeEventListener('storage', localStorageListener);
  }, [checkAuth]);

  useManagedPolling({
    poll: checkUserPermissions,
    pollInterval: PERMISSIONS_POLL_INTERVAL,
    enabled: user !== null,
  });

  if (isLoading) {
    return (
      <Backdrop open>
        <CircularProgress />
      </Backdrop>
    );
  }

  /* eslint-disable react/jsx-no-constructed-context-values */
  return (
    <AuthContext.Provider
      value={{
        user,
        mfaGate,
        backupCodes,
        login,
        completeMfa,
        setBackupCodes,
        logout,
        isLoginLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
